import { useCallback, useEffect, useMemo } from "react";
import { Alert, Button, Card, Empty, List, Skeleton, Space, Tag, Typography, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import OlitaskHeaderIcon from "components/olitask/OlitaskHeaderIcon";
import { useAuth } from "contexts/AuthContext";
import useActionables from "hooks/userManagement/useUserActionables";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";
import "./OlitaskInboxPages.css";

const { Text, Title } = Typography;

const isUnread = (status) => ["NEW", "DELIVERED", "UNREAD"].includes(String(status || "").toUpperCase());

const typeIcon = (type) => {
  const normalized = String(type || "").toUpperCase();
  if (normalized.includes("CHAT")) return "chat";
  if (normalized.includes("PAYMENT") || normalized.includes("PAYOUT")) return "mail";
  if (normalized.includes("TASK")) return "helper";
  if (normalized.includes("PROFILE") || normalized.includes("IDENTITY")) return "person";
  return "flash";
};

const timeText = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Just now";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function ActionablesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const {
    actionables,
    loading,
    error,
    fetchUserActionables,
    updateActionable,
  } = useActionables();

  const loadActionables = useCallback(() => {
    if (!actorId) return;
    fetchUserActionables(actorId, 0, 50);
  }, [actorId, fetchUserActionables]);

  useEffect(() => {
    loadActionables();
  }, [loadActionables]);

  const unreadCount = useMemo(
    () => actionables.filter((item) => isUnread(item.status)).length,
    [actionables],
  );

  const openActionable = async (item) => {
    if (item?.id && isUnread(item.status)) {
      await updateActionable(item.id, { status: "READ" });
    }

    if (item?.screen === "TaskDetails" && item?.actionItemId) {
      navigate(paths.myTasks);
      return;
    }
    if (item?.screen === "Notification") {
      navigate(paths.notifications("ACTIVITY"));
      return;
    }
    if (item?.deepLink || item?.url) {
      window.location.href = item.deepLink || item.url;
      return;
    }
    message.info("No destination was provided for this actionable.");
  };

  return (
    <div className="olitask-inbox-page">
      <div className="olitask-inbox-topbar">
        <Space align="center">
          <span className="olitask-inbox-title-icon">
            <OlitaskHeaderIcon name="flash" size={24} />
          </span>
          <div>
            <Title level={2} className="olitask-inbox-title">Actionables</Title>
            <Text className="olitask-inbox-subtitle">Items that need your attention in Olitask.</Text>
          </div>
        </Space>
        <Button className="olitask-inbox-refresh" icon={<ReloadOutlined />} onClick={loadActionables}>
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert showIcon type="error" message="Unable to load actionables" description={error} />
      ) : null}

      <Card className="olitask-inbox-card" bodyStyle={{ padding: 12 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <List
            dataSource={actionables}
            locale={{ emptyText: <Empty description="No actionables available." /> }}
            renderItem={(item) => {
              const unread = isUnread(item.status);
              return (
                <List.Item
                  className={`olitask-inbox-row${unread ? " is-unread" : ""}`}
                  onClick={() => openActionable(item)}
                >
                  <span className="olitask-inbox-row-icon">
                    <OlitaskHeaderIcon name={typeIcon(item.actionItemType)} size={30} />
                  </span>
                  <span className="olitask-inbox-row-copy">
                    <span className="olitask-inbox-row-head">
                      <Text className="olitask-inbox-row-type">
                        {String(item.actionItemType || "SYSTEM").replace(/_/g, " ")}
                      </Text>
                      {unread ? <Tag color="gold">NEW</Tag> : null}
                    </span>
                    <Text className="olitask-inbox-row-message">
                      {item.message || "No message provided."}
                    </Text>
                    <Text className="olitask-inbox-row-time">{timeText(item.createdAt)}</Text>
                  </span>
                  <OlitaskHeaderIcon className="olitask-inbox-row-tail" name="chevron" size={18} />
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {unreadCount > 0 ? (
        <Text className="olitask-inbox-footnote" style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
          {unreadCount} unread actionable{unreadCount === 1 ? "" : "s"}
        </Text>
      ) : null}
    </div>
  );
}
